import get from 'lodash.get';
import kindOf from 'kind-of';
import url from 'url';

import { formatDuration } from '../../lib/DateTime';
import { imageAltForItem } from '../../lib/imageAlt';

export const getVideoItemTitle = (item) => {
  const videoTitle = get(item, 'computedValues.headline');

  return videoTitle;
};

export const getVideoItemDuration = (item) => {
  const videoDuration = get(item, 'item.duration');

  const formattedVideoDuration = formatDuration(videoDuration);

  return formattedVideoDuration;
};

export const getVideoItemURL = (item, playlistID) => {
  const videoURLString = get(item, 'computedValues.url');
  const isExpectedDataType = kindOf(videoURLString) === 'string';
  if (!isExpectedDataType) {
    return '';
  }

  const parsedURL = url.parse(videoURLString);
  if (parsedURL.query) {
    parsedURL.query.playlist = playlistID;
  } else {
    parsedURL.query = {
      playlist: playlistID,
    };
  }
  const modifiedURL = url.format(parsedURL);

  return modifiedURL;
};

export const getVideoItemImagePreviewURL = (item) => {
  const previewImageURL = get(item, 'computedValues.url');

  return previewImageURL;
};

export const getVideoItemImageURL = (item) => {
  const imageURL = get(item, 'computedValues.teaseImage.url.primary');

  return imageURL;
};

export const transformVideoItem = (item, playlistID) => {
  const videoTitle = getVideoItemTitle(item);
  const videoDuration = getVideoItemDuration(item);
  const videoURL = getVideoItemURL(item, playlistID);

  const imageURL = getVideoItemImageURL(item);
  const imagePreviewURL = getVideoItemImagePreviewURL(item);
  const imageAltText = imageAltForItem(item);

  const transformedItem = {
    imageURL,
    imagePreviewURL,
    imageAltText,
    videoTitle,
    videoDuration,
    videoURL,
  };

  return transformedItem;
};

export const transformVideoItems = (items, playlistID) => {
  const itemsIsInvalidType = !Array.isArray(items);
  if (itemsIsInvalidType) {
    return [];
  }
  const nonNullItems = items.filter((item) => item !== null);
  const transformedItems = nonNullItems.map((item) => transformVideoItem(item, playlistID));

  return transformedItems;
};

export const getPackageHeaderHeading = (onTheShowHeaderPackageProps) => {
  const headerHeading = get(onTheShowHeaderPackageProps, 'metadata.title');

  return headerHeading;
};

export const getPackageHeaderListHeading = (onTheShowHeaderPackageProps) => {
  const headerListHeading = get(onTheShowHeaderPackageProps, 'metadata.linkListTitle');

  return headerListHeading;
};

export const transformHeaderListItems = (headerListItems) => {
  const transformedHeaderListItems = headerListItems.map((item) => ({
    text: item.linkTitle,
    href: item.linkUrl,
  }));

  return transformedHeaderListItems;
};

export const getPackageHeaderListItems = (onTheShowHeaderPackageProps) => {
  const items = get(onTheShowHeaderPackageProps, 'metadata.links', []);

  const headerListItems = transformHeaderListItems(items);

  return headerListItems;
};

export const computeShouldShowHeaderList = (heading, listItems) => {
  const hasHeaderListHeading = !!heading;
  const hasHeaderListItems = !!listItems;

  const shouldShowHeaderList = (hasHeaderListHeading && hasHeaderListItems);

  return shouldShowHeaderList;
};

export const getSeeMoreContentText = (onTheShowHeaderPackageProps) => {
  const seeMoreContentText = get(onTheShowHeaderPackageProps, 'metadata.seeAllText');

  return seeMoreContentText;
};

export const getSeeMoreContentURL = (onTheShowHeaderPackageProps) => {
  const seeMoreContentURL = get(onTheShowHeaderPackageProps, 'metadata.seeAllUrl');

  return seeMoreContentURL;
};

export const getVideoListPlaylistID = (onTheShowPlaylistPackageProps) => {
  const autoFillPlaylistIDs = get(onTheShowPlaylistPackageProps, 'autofill.values');

  let [videoListPlaylistID] = autoFillPlaylistIDs;
  if (!videoListPlaylistID) {
    videoListPlaylistID = '';
  }

  return videoListPlaylistID;
};

export const getVideoListItems = (onTheShowPlaylistPackageProps) => {
  const playlistID = getVideoListPlaylistID(onTheShowPlaylistPackageProps);
  const items = get(onTheShowPlaylistPackageProps, 'items');

  const videoListItems = transformVideoItems(items, playlistID);

  return videoListItems;
};

export const getVideoListHeading = (onTheShowPlaylistPackageProps) => {
  const videoListHeading = get(onTheShowPlaylistPackageProps, 'metadata.playlistTitle');

  return videoListHeading;
};

export const getVideoListDescription = (onTheShowPlaylistPackageProps) => {
  const videoListDescription = get(onTheShowPlaylistPackageProps, 'metadata.playlistDescription');

  return videoListDescription;
};

export const getVideoListHeadingURL = (onTheShowPlaylistPackageProps, fallBackURL) => {
  let videoListHeadingURL = get(onTheShowPlaylistPackageProps, 'metadata.playlistUrl');
  if (!videoListHeadingURL) {
    videoListHeadingURL = fallBackURL;
  }

  return videoListHeadingURL;
};

export const computeShouldShowVideoList = (videoItems) => {
  const hasVideoItems = Array.isArray(videoItems) && (videoItems.length > 0);
  const shouldShowVideoList = (hasVideoItems);

  return shouldShowVideoList;
};

export const computeShouldShowVideoListHeader = (heading, description) => {
  const hasvideoListHeading = !!heading;
  const hasvideoListDescription = !!description;

  const shouldShowVideoListHeader = (hasvideoListHeading || hasvideoListDescription);

  return shouldShowVideoListHeader;
};

export const transformOnTheShowPackagesToUIProps = (
  onTheShowHeaderPackage, onTheShowPlaylist1Package, onTheShowPlaylist2Package,
) => {
  const headerHeading = getPackageHeaderHeading(onTheShowHeaderPackage);

  const headerListHeading = getPackageHeaderListHeading(onTheShowHeaderPackage);
  const headerListItems = getPackageHeaderListItems(onTheShowHeaderPackage);

  const shouldShowHeaderList = computeShouldShowHeaderList(headerListHeading, headerListItems);

  const seeMoreContentText = getSeeMoreContentText(onTheShowHeaderPackage);
  const seeMoreContentURL = getSeeMoreContentURL(onTheShowHeaderPackage);

  const videoListOneItems = getVideoListItems(onTheShowPlaylist1Package);
  const videoListOneHeading = getVideoListHeading(onTheShowPlaylist1Package);
  const videoListOneDescription = getVideoListDescription(onTheShowPlaylist1Package);

  const videoListOneHeadingURL = getVideoListHeadingURL(
    onTheShowPlaylist1Package, seeMoreContentURL,
  );

  const shouldShowVideoListOne = computeShouldShowVideoList(videoListOneItems);
  const shouldShowVideoListOneHeader = computeShouldShowVideoListHeader(
    videoListOneHeading, videoListOneDescription,
  );

  const videoListTwoItems = getVideoListItems(onTheShowPlaylist2Package);
  const videoListTwoHeading = getVideoListHeading(onTheShowPlaylist2Package);
  const videoListTwoDescription = getVideoListDescription(onTheShowPlaylist2Package);
  const videoListTwoHeadingURL = getVideoListHeadingURL(
    onTheShowPlaylist2Package, seeMoreContentURL,
  );

  const shouldShowVideoListTwo = computeShouldShowVideoList(videoListTwoItems);
  const shouldShowVideoListTwoHeader = computeShouldShowVideoListHeader(
    videoListTwoHeading, videoListTwoDescription,
  );

  return {
    headerHeading,

    headerListHeading,
    headerListItems,
    shouldShowHeaderList,

    videoListOneItems,
    videoListOneHeading,
    videoListOneDescription,
    videoListOneHeadingURL,
    shouldShowVideoListOne,
    shouldShowVideoListOneHeader,

    videoListTwoItems,
    videoListTwoHeading,
    videoListTwoDescription,
    videoListTwoHeadingURL,
    shouldShowVideoListTwo,
    shouldShowVideoListTwoHeader,

    seeMoreContentURL,
    seeMoreContentText,
  };
};
